import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import ActionButton from '@repo/ui/action-button'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import DataTable, { Column } from '@repo/ui/data-table'
import { DeleteIcon, MessageIcon, PenIcon, PlusIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Loader from '@repo/ui/loader'
import Modal from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { createTeflOlComment } from '../../../api/tefl/tefl-ol-comments/create-tefl-ol-comment'
import { deleteTeflOlComments } from '../../../api/tefl/tefl-ol-comments/delete-tefl-ol-comment'
import { getTeflOlComments } from '../../../api/tefl/tefl-ol-comments/get-tefl-ol-comments'
import { updateTeflOlComment } from '../../../api/tefl/tefl-ol-comments/update-tefl-ol-comment'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { TeflOlComment } from '../../../lib/types/models/tefl-ol-comment/tefl-ol-comment'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import ActionTableWrapper from '../../elements/action-table-wrapper'
import TeflOlCommentForm from './tefl-ol-comment-form'
import { mapTeflOlCommentToInput } from './utils'

export type Props = {
  id: number
}

const TeflOlCommentTable = ({ id }: Props) => {
  const queryClient = useQueryClient()

  const { modalState, closeModel, selectedData, toggleModal } = useModalManager<
    'create' | 'edit' | 'delete',
    TeflOlComment
  >(['create', 'edit', 'delete'])

  const { data: hostFamilyOlComments } = useQuery({
    queryKey: ['tefl-ol-comments'],
    queryFn: () => getTeflOlComments({ teflId: id })
  })

  const deleteComment = useMutation({
    mutationFn: deleteTeflOlComments,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Comment deleted successfully')
      queryClient.invalidateQueries({
        queryKey: ['tefl-ol-comments']
      })
      closeModel('delete')
    }
  })

  const createComment = useMutation({
    mutationFn: createTeflOlComment,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Comment added successfully')
      queryClient.invalidateQueries({
        queryKey: ['tefl-ol-comments']
      })
      closeModel('create')
    }
  })

  const updateComment = useMutation({
    mutationFn: updateTeflOlComment,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Comment updated successfully')
      queryClient.invalidateQueries({
        queryKey: ['tefl-ol-comments']
      })
      closeModel('edit')
    }
  })

  const columns: Column<TeflOlComment>[] = [
    {
      enableSorting: true,
      header: 'Comments',
      accessorKey: 'comment',
      cell: ({ row }) => (
        <Typography
          variant="tag"
          color={tailwindColors.black.DEFAULT}
          className="text-wrap px-3 py-4"
        >
          {row.original.comment}
        </Typography>
      )
    },
    {
      enableSorting: true,
      header: 'Date & time',
      accessorKey: 'createdAt',
      cell: ({ row }) => (
        <Badge
          text={`${DateFormatter.DDMMYYYY(row.original.updatedAt)} - ${DateFormatter.HHmm(row.original.updatedAt)}`}
          variant="grey"
        />
      )
    },
    {
      enableSorting: false,
      header: '',
      columnHeaderClassName: 'w-12',
      exportable: false,
      accessorKey: 'id',
      cell: ({ row }) => (
        <div className={'flex gap-2 pr-3'}>
          <ActionButton
            variant={'grey'}
            icon={PenIcon}
            onClick={() => {
              toggleModal('edit', row.original)
            }}
          />
          <ActionButton
            variant={'danger'}
            icon={DeleteIcon}
            onClick={() => {
              toggleModal('delete', row.original)
            }}
          />
        </div>
      )
    }
  ]

  if (!hostFamilyOlComments) return <Loader center />

  return (
    <InnerBox className=" rounded-lg border border-grey-light p-0">
      <ActionTableWrapper
        title={'OL Comments'}
        transparent
        actions={[
          <Button
            key={'add'}
            type="button"
            icon={PlusIcon}
            label={'Add comment'}
            variant={'outline'}
            onClick={() => toggleModal('create')}
          />
        ]}
      >
        <DataTable<TeflOlComment>
          manualSorting={false}
          columns={columns}
          data={hostFamilyOlComments?.data ?? []}
          rowCount={hostFamilyOlComments?.data.length ?? 0}
          paginated={false}
        />

        <Modal
          isOpen={modalState.create}
          title={'Add a comment'}
          size="medium"
          hideActions
          icon={MessageIcon}
          onCancel={() => closeModel('create')}
        >
          <TeflOlCommentForm
            buttonsConfig={modalButtonConfig({
              onCancel: () => closeModel('create'),
              onConfirm: async (values) => {
                const { updatedAt: _updatedAt, ...rest } = values
                await createComment.mutateAsync({ ...rest, teflId: id })
              },
              isEnglish: true
            })}
          />
        </Modal>

        {selectedData && (
          <Modal
            isOpen={modalState.edit}
            title={'Edit comment'}
            hideActions
            icon={MessageIcon}
            size="medium"
            onCancel={() => closeModel('edit')}
          >
            <TeflOlCommentForm
              defaultValues={mapTeflOlCommentToInput(selectedData)}
              buttonsConfig={modalButtonConfig({
                onCancel: () => closeModel('edit'),
                onConfirm: async (values) => {
                  const { updatedAt: _updatedAt, ...rest } = values
                  await updateComment.mutateAsync({ ...rest, id: selectedData.id, teflId: id })
                },
                isEnglish: true
              })}
            />
          </Modal>
        )}

        {selectedData && (
          <Modal
            actionButtonsOptions={{
              confirmText: 'Delete',
              cancelText: 'Cancel',
              onConfirm: async () => {
                await deleteComment.mutateAsync({ teflId: id, ids: [selectedData.id] })
              }
            }}
            onCancel={() => toggleModal('delete')}
            isOpen={modalState.delete}
            title={'Are you sure you want to delete this comment?'}
            icon={DeleteIcon}
            isLoading={deleteComment.isPending}
            variant={'destructive'}
          />
        )}
      </ActionTableWrapper>
    </InnerBox>
  )
}

export default TeflOlCommentTable
